import { useEffect, useRef, useState } from 'react'
import { Check, Copy, Sparkles } from 'lucide-react'
import type { TenantClaudeAsk } from '@omni/instance'

// The bridge from any Command Centre page to Claude Code (Sam, 10 Oct 2026):
// the page is where you LOOK, Claude is where you ASK. Every button copies a
// ready request in plain words and (optionally) opens Claude, so the next
// step from any page is one paste. Generic version of ONA's lib/onaClaude.

export const CLAUDE_URL = 'https://claude.ai/code'

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const el = document.createElement('textarea')
      el.value = text
      el.setAttribute('readonly', '')
      el.style.position = 'fixed'
      el.style.opacity = '0'
      document.body.appendChild(el)
      el.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(el)
      return ok
    } catch {
      return false
    }
  }
}

/**
 * Fills {placeholders} from the page's address (?b=Nicabate fills {brand}),
 * and leaves a readable [bracket] where the page has nothing to offer.
 */
export function fillAsk(say: string, search = typeof window === 'undefined' ? '' : window.location.search): string {
  const q = new URLSearchParams(search)
  const vals: Record<string, string | null> = {
    brand: q.get('b') ?? q.get('brand'),
    company: q.get('company') ? (q.get('company') === 'au' ? 'Australia' : 'New Zealand') : null,
  }
  return say.replace(/\{(\w+)\}/g, (_, k: string) => vals[k] ?? `[${k}]`)
}

/** Copies the request (and opens Claude unless open=false), then says what to do next, in place. */
export function AskClaudeButton({
  ask,
  label = 'Ask Claude',
  open = true,
  quiet = false,
}: {
  ask: string
  label?: string
  open?: boolean
  quiet?: boolean
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const onClick = async () => {
    // Open first, inside the click, or the browser treats it as a pop-up.
    if (open) window.open(CLAUDE_URL, '_blank', 'noopener')
    const ok = await copyText(ask)
    setState(ok ? 'copied' : 'failed')
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setState('idle'), 5000)
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        title={ask}
        className={`oc-ask-btn ${quiet ? 'oc-ask-quiet' : ''}`}
      >
        {state === 'copied' ? <Check size={14} /> : open ? <Sparkles size={14} /> : <Copy size={14} />}
        {state === 'copied' ? 'Copied' : label}
      </button>
      {state !== 'idle' && (
        <span role="status" className="text-[12px]" style={{ color: state === 'copied' ? 'var(--color-text-secondary)' : '#b42318' }}>
          {state === 'copied'
            ? open
              ? 'Paste it into Claude (Ctrl+V) and press Enter.'
              : 'Now paste it into Claude (Ctrl+V).'
            : 'Could not copy. Type it into Claude instead.'}
        </span>
      )}
    </span>
  )
}

/** One request as a card: the job, the words to say, what happens, and Copy. */
export function AskCard({ a, open = false }: { a: TenantClaudeAsk; open?: boolean }) {
  const say = fillAsk(a.say)
  return (
    <div className="oc-ask-card">
      <span className="oc-ask-title">{a.title}</span>
      <span className="oc-ask-say">{say}</span>
      <span className="oc-ask-why">{a.why}</span>
      <span>
        <AskClaudeButton ask={say} label={open ? 'Ask Claude' : 'Copy'} open={open} quiet />
      </span>
    </div>
  )
}
